import { useState, useRef, useEffect } from 'react'
import api from '../../api/axios'

const STEPS = ['Conexão', 'Selecionar Leilões', 'Importar']
const PER_PAGE = 20

export default function ImportacaoLeiloesBulk() {
  const [step, setStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState('')

  // Step 0
  const [creds, setCreds] = useState({ email: '', password: '' })

  // Step 1 – lista de leilões
  const [leiloes, setLeiloes] = useState([])
  const [jaImportados, setJaImportados] = useState(new Set()) // ids antigos já no histórico
  const [selecionados, setSelecionados] = useState(new Set())
  const [busca, setBusca] = useState('')
  const [filtroPeriodo, setFiltroPeriodo] = useState('')
  const [filtroStatus, setFiltroStatus] = useState('todos') // todos | pendentes | importados
  const [pagina, setPagina] = useState(1)

  // Step 2 – org + execução
  const [organizadores, setOrganizadores] = useState([])
  const [orgSelecionada, setOrgSelecionada] = useState(null)
  const [buscaOrg, setBuscaOrg] = useState('')
  const [loadingOrg, setLoadingOrg] = useState(false)
  const [fluxoComissaoPadrao, setFluxoComissaoPadrao] = useState('primeira_parcela')
  const [tamanhoBatch, setTamanhoBatch] = useState(25)
  const [batchInfo, setBatchInfo] = useState(null) // null | { atual: 1, total: 3 }
  const [forcarAtualizacao, setForcarAtualizacao] = useState(false)
  const [dryRunOk, setDryRunOk] = useState(false)
  const [jobId, setJobId] = useState(null)
  const [job, setJob] = useState(null)
  const pollingRef = useRef(null)
  const [errosModal, setErrosModal] = useState(null) // { nome, erros[] } | null

  // ── Step 0: buscar leilões ────────────────────────────────────────────────
  async function handleBuscarLeiloes(e) {
    e.preventDefault()
    setErro('')
    setLoading(true)
    try {
      const [{ data }, { data: hist }] = await Promise.all([
        api.post('super-admin/importacao/buscar-leiloes', creds, { timeout: 30_000 }),
        api.get(`super-admin/importacao/historico?email_sistema_antigo=${encodeURIComponent(creds.email)}`).catch(() => ({ data: [] })),
      ])
      setLeiloes(data.leiloes || [])
      setJaImportados(new Set((hist || []).map(h => String(h.leilao_id_antigo))))
      setSelecionados(new Set())
      setBusca('')
      setFiltroPeriodo('')
      setFiltroStatus('todos')
      setPagina(1)
      setStep(1)
    } catch (err) {
      const detail = err.response?.data?.detail
      setErro(typeof detail === 'string' ? detail : (err.message || 'Erro ao conectar'))
    } finally {
      setLoading(false)
    }
  }

  // ── Filtro + paginação ────────────────────────────────────────────────────
  const periodos = [...new Set(
    leiloes.flatMap(l => [...l.nome.matchAll(/\b(20\d{2})\b/g)].map(m => m[1]))
  )].sort().reverse()

  const filtrados = leiloes.filter(l => {
    const nomeOk = !busca || l.nome.toLowerCase().includes(busca.toLowerCase())
    const periodoOk = !filtroPeriodo || l.nome.includes(filtroPeriodo)
    const importado = jaImportados.has(String(l.id))
    const statusOk = filtroStatus === 'todos' || (filtroStatus === 'importados' ? importado : !importado)
    return nomeOk && periodoOk && statusOk
  })

  const totalPags = Math.ceil(filtrados.length / PER_PAGE)
  const paginados = filtrados.slice((pagina - 1) * PER_PAGE, pagina * PER_PAGE)

  function toggleSelecionado(id) {
    setSelecionados(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  function toggleTodosFiltrados() {
    const ids = filtrados.map(l => l.id)
    const todosMarcados = ids.every(id => selecionados.has(id))
    setSelecionados(prev => {
      const next = new Set(prev)
      ids.forEach(id => todosMarcados ? next.delete(id) : next.add(id))
      return next
    })
  }

  // ── Step 1 → Step 2: carregar orgs ───────────────────────────────────────
  async function handleAvancarParaImportar() {
    setErro('')
    if (selecionados.size === 0) { setErro('Selecione ao menos um leilão'); return }
    setLoadingOrg(true)
    try {
      const { data } = await api.get('/organizadores?per_page=100&status=ativo')
      setOrganizadores(data.data || [])
      setStep(2)
    } catch {
      setErro('Erro ao carregar organizadores')
    } finally {
      setLoadingOrg(false)
    }
  }

  const orgsFiltradas = organizadores.filter(o =>
    !buscaOrg || (o.name || '').toLowerCase().includes(buscaOrg.toLowerCase())
  )

  // ── Polling do job (com retry — tolera falhas de rede transitórias) ────────
  function pollJobUntilDone(id) {
    let falhasConsecutivas = 0
    const MAX_FALHAS = 6 // ~15s de tolerância (6 × 2.5s)
    return new Promise((resolve, reject) => {
      const iv = setInterval(async () => {
        try {
          const { data } = await api.get(`super-admin/importacao/status-leiloes-bulk/${id}`)
          falhasConsecutivas = 0
          setJob(prev => ({ ...prev, ...data }))
          if (data.status !== 'rodando') {
            clearInterval(iv)
            pollingRef.current = null
            resolve(data)
          }
        } catch (e) {
          falhasConsecutivas++
          if (falhasConsecutivas >= MAX_FALHAS) {
            clearInterval(iv)
            pollingRef.current = null
            reject(e)
          }
          // Falha transitória — tenta no próximo intervalo
        }
      }, 2500)
      pollingRef.current = iv
    })
  }

  useEffect(() => () => clearInterval(pollingRef.current), [])

  // ── Executar (dry-run ou real) — suporta batches ──────────────────────────
  async function handleExecutar(isDry) {
    if (!orgSelecionada) { setErro('Selecione um organizador'); return }
    setErro('')
    setLoading(true)
    setJob(null)
    setJobId(null)
    setBatchInfo(null)

    const ids = [...selecionados]
    const chunks = []
    for (let i = 0; i < ids.length; i += tamanhoBatch) chunks.push(ids.slice(i, i + tamanhoBatch))
    const emBatches = chunks.length > 1

    let acumulados = []
    try {
      for (let i = 0; i < chunks.length; i++) {
        if (emBatches) setBatchInfo({ atual: i + 1, total: chunks.length })

        const { data } = await api.post('super-admin/importacao/executar-leiloes-bulk', {
          ...creds,
          org_id: orgSelecionada.id,
          leilao_ids: chunks[i],
          importar_lotes: !isDry,
          dry_run: isDry,
          fluxo_comissao_padrao: fluxoComissaoPadrao,
          forcar_atualizacao: forcarAtualizacao,
        }, { timeout: 60_000 })

        setJobId(data.job_id)
        setJob({ status: 'rodando', total: data.total, done: 0, resultados: emBatches ? acumulados : [] })
        if (isDry) setDryRunOk(false)

        const jobData = await pollJobUntilDone(data.job_id)
        acumulados = [...acumulados, ...(jobData.resultados || [])]

        const novosImportados = (jobData.resultados || [])
          .filter(r => r.ok && !r.dry_run)
          .map(r => String(r.leilao_id_antigo))
        if (novosImportados.length > 0) setJaImportados(prev => new Set([...prev, ...novosImportados]))

        // Merge accumulated results into displayed job
        if (emBatches) setJob({ ...jobData, resultados: acumulados })
      }

      const ok = acumulados.filter(r => r.ok).length
      const erros = acumulados.length - ok
      alert(erros > 0
        ? `Importação concluída: ${ok} leilão(ões) importados, ${erros} com erro.`
        : `✓ ${ok} leilão(ões) importados com sucesso!`)
    } catch (err) {
      const detail = err.response?.data?.detail
      setErro(typeof detail === 'string' ? detail : JSON.stringify(detail) || err.message)
    } finally {
      setLoading(false)
    }
  }

  const progresso = job ? Math.round((job.done / (job.total || 1)) * 100) : 0
  const isDone = job?.status === 'concluido' || job?.status === 'erro'
  const okCount = job?.resultados?.filter(r => r.ok).length ?? 0
  const errCount = job?.resultados?.filter(r => !r.ok).length ?? 0

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-2">Importação em Lote de Leilões</h1>
      <p className="text-sm text-gray-500 mb-6">Exclusivo SuperAdmin — importa múltiplos leilões do sistema antigo com comissões e leiloeiro</p>

      {/* Steps */}
      <div className="flex gap-2 mb-6">
        {STEPS.map((s, i) => (
          <div key={i} className={`flex items-center gap-1 text-sm font-medium px-3 py-1 rounded-full ${i === step ? 'bg-blue-100 text-blue-700' : i < step ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
            <span>{i < step ? '✓' : i + 1}.</span> {s}
          </div>
        ))}
      </div>

      {erro && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-4 text-sm">{erro}</div>
      )}

      {/* ── PASSO 0: Conexão ── */}
      {step === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-md">
          <h2 className="font-semibold text-gray-700 mb-4">Credenciais do sistema antigo</h2>
          <form onSubmit={handleBuscarLeiloes} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
              <input type="email" required value={creds.email}
                onChange={e => setCreds(p => ({ ...p, email: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Senha</label>
              <input type="password" required value={creds.password}
                onChange={e => setCreds(p => ({ ...p, password: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <button type="submit" disabled={loading}
              className="w-full bg-blue-600 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {loading ? 'Conectando...' : 'Conectar e listar leilões'}
            </button>
          </form>
        </div>
      )}

      {/* ── PASSO 1: Selecionar leilões ── */}
      {step === 1 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="font-semibold text-gray-700">Selecionar leilões para importar</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {filtrados.length} leilões encontrados · {selecionados.size} selecionados
                {jaImportados.size > 0 && <span className="ml-2 text-green-600">· {jaImportados.size} já importados</span>}
              </p>
              {selecionados.size > 0 && (
                <p className={`text-xs mt-1 font-medium ${selecionados.size > 50 ? 'text-red-500' : selecionados.size > 30 ? 'text-yellow-600' : 'text-green-600'}`}>
                  {selecionados.size <= 30
                    ? `✓ Quantidade segura (~${Math.round(selecionados.size * 0.75)} min estimados)`
                    : selecionados.size <= 50
                      ? `⚠ Acima do ideal — recomendamos até 30 por vez (~${Math.round(selecionados.size * 0.75)} min)`
                      : `⚠ Muitos leilões de uma vez — risco de bloqueio no sistema antigo. Prefira lotes de até 50.`}
                </p>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              <input type="text" placeholder="Buscar por nome..." value={busca}
                onChange={e => { setBusca(e.target.value); setPagina(1) }}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <select value={filtroStatus} onChange={e => { setFiltroStatus(e.target.value); setPagina(1) }}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="todos">Todos</option>
                <option value="pendentes">Não importados</option>
                <option value="importados">Já importados</option>
              </select>
              {periodos.length > 0 && (
                <select value={filtroPeriodo} onChange={e => { setFiltroPeriodo(e.target.value); setPagina(1) }}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">Todos os anos</option>
                  {periodos.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              )}
              <button onClick={toggleTodosFiltrados}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm hover:bg-gray-50">
                {filtrados.every(l => selecionados.has(l.id)) ? 'Desmarcar filtrados' : 'Marcar filtrados'}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-600 text-xs uppercase">
                  <th className="px-3 py-2 text-left w-8">
                    <input type="checkbox"
                      checked={paginados.length > 0 && paginados.every(l => selecionados.has(l.id))}
                      onChange={() => {
                        const ids = paginados.map(l => l.id)
                        const allChecked = ids.every(id => selecionados.has(id))
                        setSelecionados(prev => {
                          const next = new Set(prev)
                          ids.forEach(id => allChecked ? next.delete(id) : next.add(id))
                          return next
                        })
                      }} />
                  </th>
                  <th className="px-3 py-2 text-left">ID Antigo</th>
                  <th className="px-3 py-2 text-left">Nome do leilão</th>
                  <th className="px-3 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {paginados.map(l => {
                  const importado = jaImportados.has(String(l.id))
                  return (
                    <tr key={l.id} onClick={() => toggleSelecionado(l.id)}
                      className={`border-t border-gray-100 cursor-pointer hover:bg-blue-50 transition ${selecionados.has(l.id) ? 'bg-blue-50' : ''}`}>
                      <td className="px-3 py-2">
                        <input type="checkbox" checked={selecionados.has(l.id)} onChange={() => toggleSelecionado(l.id)} onClick={e => e.stopPropagation()} />
                      </td>
                      <td className="px-3 py-2 text-gray-500 font-mono text-xs">{l.id}</td>
                      <td className="px-3 py-2 text-gray-800">{l.nome}</td>
                      <td className="px-3 py-2">
                        {importado
                          ? <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">✓ Importado</span>
                          : <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Pendente</span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          {totalPags > 1 && (
            <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
              <span>Página {pagina} de {totalPags}</span>
              <div className="flex gap-2">
                <button onClick={() => setPagina(p => Math.max(1, p - 1))} disabled={pagina === 1}
                  className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50">← Ant</button>
                <button onClick={() => setPagina(p => Math.min(totalPags, p + 1))} disabled={pagina === totalPags}
                  className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50">Próx →</button>
              </div>
            </div>
          )}

          <div className="mt-5 flex gap-3">
            <button onClick={() => setStep(0)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
              ← Voltar
            </button>
            <button onClick={handleAvancarParaImportar} disabled={selecionados.size === 0 || loadingOrg}
              className="px-5 py-2 text-sm bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
              {loadingOrg ? 'Carregando...' : `Avançar com ${selecionados.size} leilão(ões) →`}
            </button>
          </div>
        </div>
      )}

      {/* ── PASSO 2: Org + execução ── */}
      {step === 2 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-700 mb-4">
            Importar {selecionados.size} leilão(ões) selecionado(s)
          </h2>

          {/* Seletor de organizador */}
          {!orgSelecionada ? (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Selecione o organizador destino</label>
              <input type="text" placeholder="Buscar organizador..." value={buscaOrg}
                onChange={e => setBuscaOrg(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <div className="border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
                {orgsFiltradas.length === 0
                  ? <p className="text-sm text-gray-400 p-4 text-center">Nenhum organizador encontrado</p>
                  : orgsFiltradas.map(o => (
                    <button key={o.id} onClick={() => setOrgSelecionada(o)}
                      className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-100 last:border-0 text-sm transition">
                      <span className="font-medium text-gray-800">{o.name}</span>
                      {o.cidade && <span className="text-gray-500 ml-2 text-xs">{o.cidade}</span>}
                    </button>
                  ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 mb-6 p-3 bg-blue-50 rounded-lg">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-800">{orgSelecionada.name}</p>
                <p className="text-xs text-gray-500">Organizador selecionado</p>
              </div>
              <button onClick={() => { setOrgSelecionada(null); setJob(null); setJobId(null); setDryRunOk(false); setFluxoComissaoPadrao('primeira_parcela') }}
                className="text-xs text-blue-600 hover:underline">Trocar</button>
            </div>
          )}

          {/* Fluxo de comissão padrão */}
          {orgSelecionada && (
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fluxo de comissão padrão
                <span className="ml-1 font-normal text-gray-400 text-xs">(usado quando não detectado no sistema antigo)</span>
              </label>
              <select
                value={fluxoComissaoPadrao}
                onChange={e => setFluxoComissaoPadrao(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="primeira_parcela">Comissão na primeira parcela</option>
                <option value="separada">Comissão separada</option>
                <option value="gta_nota">GTA / Nota</option>
                <option value="parcelada">Comissão parcelada</option>
              </select>
            </div>
          )}

          {/* Tamanho do batch */}
          {orgSelecionada && selecionados.size > tamanhoBatch && (
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantos leilões por vez
                <span className="ml-1 font-normal text-gray-400 text-xs">(importa em sequência, evita bloqueio)</span>
              </label>
              <div className="flex items-center gap-3">
                <select
                  value={tamanhoBatch}
                  onChange={e => setTamanhoBatch(Number(e.target.value))}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={10}>10 por vez</option>
                  <option value={15}>15 por vez</option>
                  <option value={20}>20 por vez</option>
                  <option value={25}>25 por vez (recomendado)</option>
                  <option value={30}>30 por vez</option>
                </select>
                <span className="text-xs text-gray-500">
                  {Math.ceil(selecionados.size / tamanhoBatch)} lote(s) de importação para {selecionados.size} leilões
                </span>
              </div>
            </div>
          )}

          {/* Forçar atualização */}
          {orgSelecionada && (
            <div className="mb-5 flex items-start gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <input
                type="checkbox"
                id="forcar-atualizacao"
                checked={forcarAtualizacao}
                onChange={e => setForcarAtualizacao(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-yellow-600"
              />
              <label htmlFor="forcar-atualizacao" className="text-sm cursor-pointer">
                <span className="font-medium text-yellow-800">Forçar atualização de lotes existentes</span>
                <span className="block text-xs text-yellow-700 mt-0.5">
                  Reimporta e corrige lotes que já foram importados com dados errados (status, valor, comprador).
                  Use ao corrigir um import anterior com problemas.
                </span>
              </label>
            </div>
          )}

          {/* Ações */}
          {orgSelecionada && !jobId && (
            <div className="flex gap-3">
              <button onClick={() => handleExecutar(true)} disabled={loading}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                {loading ? 'Verificando...' : '🔍 Dry-run (verificar sem salvar)'}
              </button>
              <button onClick={() => handleExecutar(false)} disabled={loading || (!dryRunOk && false)}
                className="px-5 py-2 text-sm bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50">
                {loading ? 'Iniciando...' : '🚀 Importar leilões'}
              </button>
            </div>
          )}

          {/* Progresso */}
          {job && (
            <div className="mt-6">
              {batchInfo && (
                <div className="flex items-center gap-2 mb-3 text-sm">
                  <span className="bg-blue-100 text-blue-700 font-medium px-3 py-1 rounded-full">
                    Lote {batchInfo.atual} de {batchInfo.total}
                  </span>
                  <span className="text-gray-500">
                    ({(batchInfo.atual - 1) * tamanhoBatch + (job.done || 0)} / {selecionados.size} leilões no total)
                  </span>
                </div>
              )}

              {/* Barra de progresso com % */}
              <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                <span className="font-medium">{job.done} / {job.total} leilões processados</span>
                <span className={`font-bold text-base ${isDone ? (errCount === job.total ? 'text-red-600' : 'text-green-600') : 'text-blue-600'}`}>
                  {progresso}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                <div
                  className={`h-3 rounded-full transition-all duration-500 ${isDone ? (errCount > 0 && okCount === 0 ? 'bg-red-500' : 'bg-green-500') : 'bg-blue-600'}`}
                  style={{ width: `${progresso}%` }}
                />
              </div>

              {/* Leilão em andamento */}
              {job.status === 'rodando' && job.leilao_atual_id != null && (() => {
                const leilaoAtual = leiloes.find(l => String(l.id) === String(job.leilao_atual_id))
                return (
                  <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 mb-3">
                    <span className="animate-spin text-base">⟳</span>
                    <span className="font-medium">Processando:</span>
                    <span className="truncate">{leilaoAtual?.nome || `Leilão ${job.leilao_atual_id}`}</span>
                    <span className="ml-auto text-blue-500 font-medium whitespace-nowrap">
                      {job.leilao_atual_idx} / {job.total}
                    </span>
                  </div>
                )
              })()}

              {/* Status final */}
              {isDone && (
                <div className={`text-sm font-medium mb-3 ${errCount === job.total ? 'text-red-600' : 'text-green-600'}`}>
                  {job.status === 'concluido' ? (errCount > 0 ? `Concluído com ${errCount} erro(s)` : 'Concluído com sucesso') : 'Erro na importação'}
                </div>
              )}


              {isDone && (
                <div className="flex gap-4 mb-4 flex-wrap">
                  <div className="bg-green-50 rounded-lg p-3 flex-1 min-w-[120px]">
                    <div className="text-2xl font-bold text-green-700">{okCount}</div>
                    <div className="text-xs text-green-600">Importados com sucesso</div>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3 flex-1 min-w-[120px]">
                    <div className="text-2xl font-bold text-red-700">{errCount}</div>
                    <div className="text-xs text-red-600">Com erro</div>
                  </div>
                  {!job.dry_run && (
                    <div className="bg-blue-50 rounded-lg p-3 flex-1 min-w-[120px]">
                      <div className="text-2xl font-bold text-blue-700">
                        {job.resultados?.filter(r => r.ok).reduce((s, r) => s + (r.lotes_importados || 0), 0)}
                      </div>
                      <div className="text-xs text-blue-600">Lotes importados</div>
                    </div>
                  )}
                  {!job.dry_run && (
                    <div className="bg-purple-50 rounded-lg p-3 flex-1 min-w-[120px]">
                      <div className="text-2xl font-bold text-purple-700">
                        {job.resultados?.filter(r => r.ok).reduce((s, r) => s + (r.comissoes_criadas || 0), 0)}
                      </div>
                      <div className="text-xs text-purple-600">Comissões criadas</div>
                    </div>
                  )}
                </div>
              )}

              {/* Tabela de resultados */}
              {job.resultados?.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-gray-50 text-gray-600 uppercase text-xs">
                        <th className="px-2 py-2 text-left">Status</th>
                        <th className="px-2 py-2 text-left">ID Antigo</th>
                        <th className="px-2 py-2 text-left">Nome</th>
                        <th className="px-2 py-2 text-left">Data</th>
                        <th className="px-2 py-2 text-left">Cidade/UF</th>
                        <th className="px-2 py-2 text-left">Fluxo</th>
                        <th className="px-2 py-2 text-left">Leiloeiro</th>
                        <th className="px-2 py-2 text-left">Comissões</th>
                        {!job.dry_run && <th className="px-2 py-2 text-left">Lotes</th>}
                        {!job.dry_run && <th className="px-2 py-2 text-left">ID Novo</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {job.resultados.map((r, i) => (
                        <tr key={i} className={`border-t border-gray-100 ${r.ok ? '' : 'bg-red-50'}`}>
                          <td className="px-2 py-1.5">
                            {r.ok
                              ? r.ja_importado
                                ? <span className="text-yellow-500 font-medium" title="Já importado — lotes novos adicionados">↩</span>
                                : <span className="text-green-600 font-medium">✓</span>
                              : <span className="text-red-600 font-medium">✗</span>}
                          </td>
                          <td className="px-2 py-1.5 font-mono text-gray-500">{r.leilao_id_antigo}</td>
                          <td className="px-2 py-1.5 max-w-[260px] text-gray-800">
                            {r.ok
                              ? <span className="truncate block" title={r.nome}>{r.nome}</span>
                              : <span className="text-red-600 text-xs break-words">{r.erro || 'Erro desconhecido'}</span>}
                          </td>
                          <td className="px-2 py-1.5 text-gray-600">{r.data}</td>
                          <td className="px-2 py-1.5 text-gray-600">{r.cidade ? `${r.cidade}/${r.uf}` : '—'}</td>
                          <td className="px-2 py-1.5 text-gray-600">{r.fluxo_comissao || '—'}</td>
                          <td className="px-2 py-1.5 text-gray-600 max-w-[120px] truncate" title={r.leiloeiro}>{r.leiloeiro || '—'}</td>
                          <td className="px-2 py-1.5 text-gray-600">{r.comissoes_leilao ?? '—'}</td>
                          {!job.dry_run && (
                            <td className="px-2 py-1.5 text-gray-600">
                              <span className="text-green-700 font-medium">{r.lotes_importados ?? 0}</span>
                              {r.lotes_erros?.length > 0 && (
                                <button
                                  onClick={() => setErrosModal({ nome: r.nome, erros: r.lotes_erros })}
                                  className="text-red-500 ml-1 underline hover:text-red-700"
                                >({r.lotes_erros.length} err)</button>
                              )}
                            </td>
                          )}
                          {!job.dry_run && <td className="px-2 py-1.5 font-mono text-blue-600">{r.leilao_id_novo || '—'}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {isDone && !job.dry_run && (
                <div className="mt-4 flex gap-3">
                  <button onClick={() => { setStep(0); setCreds({ email: '', password: '' }); setLeiloes([]); setSelecionados(new Set()); setOrgSelecionada(null); setJob(null); setJobId(null); setDryRunOk(false) }}
                    className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                    Nova importação
                  </button>
                </div>
              )}

              {isDone && job.dry_run && (
                <div className="mt-4 flex gap-3">
                  <button onClick={() => handleExecutar(false)} disabled={loading}
                    className="px-5 py-2 text-sm bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50">
                    {loading ? 'Iniciando...' : '🚀 Confirmar importação'}
                  </button>
                </div>
              )}
            </div>
          )}

          {!jobId && (
            <button onClick={() => setStep(1)} className="mt-4 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
              ← Voltar
            </button>
          )}
        </div>
      )}
    </div>

    {errosModal && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setErrosModal(null)}>
        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between px-5 py-4 border-b">
            <div>
              <h3 className="font-semibold text-gray-900">Erros de importação de lotes</h3>
              <p className="text-xs text-gray-500 mt-0.5 truncate max-w-md">{errosModal.nome}</p>
            </div>
            <button onClick={() => setErrosModal(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
          </div>
          <div className="overflow-y-auto flex-1 px-5 py-3">
            <ul className="space-y-1.5">
              {errosModal.erros.map((e, i) => (
                <li key={i} className="text-sm text-gray-700 bg-red-50 rounded px-3 py-1.5 font-mono break-words">{e}</li>
              ))}
            </ul>
          </div>
          <div className="px-5 py-3 border-t text-right">
            <span className="text-xs text-gray-400 mr-4">{errosModal.erros.length} erro(s)</span>
            <button onClick={() => setErrosModal(null)} className="px-4 py-1.5 text-sm bg-gray-100 rounded-lg hover:bg-gray-200">Fechar</button>
          </div>
        </div>
      </div>
    )}
    </>
  )
}
